import { useCallback } from 'react';
import { useSelector } from 'react-redux';

import { type PublicClip } from '@air/api';

import { usePublicPermissions } from '~/components/PublicPermissionsProvider';
import { type GalleryFileProps } from '~/components/v2/components/shared/gallery/GalleryFile';
import { usePublicLibraryCustomFields } from '~/components/v2/hooks/usePublicLibraryCustomFields';
import { QueryParamNames } from '~/constants/search';
import { useGoToPublicAssetPage } from '~/hooks/useGoToPublicAssetPage';
import { useOpenAssetInNewTab } from '~/hooks/useOpenAssetInNewTab';
import { useURLLibraryIdSelector } from '~/hooks/useURLLibraryIdSelector';
import { AssetModalPanel } from '~/providers/AssetModalPanelContextProvider';
import { usePublicCustomFieldsContext } from '~/providers/PublicCustomFieldsProvider';
import { centralizedBoardLibraryIdSelector } from '~/store/centralizedBoard/selectors';
import {
  canSeeAssetCustomFields,
  canSeeAssetVersions,
  canViewAssetDiscussions,
} from '~/utils/permissions/assetPermissions';

export type UsePublicGalleryFileCardPropsParams = {
  file: PublicClip;
};

export type PublicGalleryFileCardProps = Pick<
  GalleryFileProps<PublicClip>,
  | 'allCustomFields'
  | 'canViewCustomFields'
  | 'onCustomFieldsClick'
  | 'onDiscussionsClick'
  | 'onFileClick'
  | 'onFileTitleCmdClick'
  | 'onVersionsClick'
  | 'shouldShowDiscussions'
  | 'showVersionsIndicator'
>;

/**
 * The public file card's BODY contract — every GalleryFile prop that shapes what the card looks
 * like and where clicking it goes, derived from the public permission/custom-field/navigation
 * hooks. Shared by PublicGalleryFile (public pages) and ShowcaseEditorGalleryFile (the
 * editor's edit canvas) so the canvas card stays pixel-identical to the public card (WYSIWYG);
 * only the menu getters — deliberately NOT part of this contract — differ between the two.
 */
export const usePublicGalleryFileCardProps = ({
  file,
}: UsePublicGalleryFileCardPropsParams): PublicGalleryFileCardProps => {
  const { permissions } = usePublicPermissions();
  const { customFields } = usePublicCustomFieldsContext();
  const { goToAssetPage } = useGoToPublicAssetPage();
  const { openAssetInNewTab } = useOpenAssetInNewTab();
  const urlLibraryId = useURLLibraryIdSelector();
  const libraryId = useSelector(centralizedBoardLibraryIdSelector);
  const { data: libraryCustomFieldsVisbility } = usePublicLibraryCustomFields({
    libraryId: libraryId || urlLibraryId,
  });
  const filteredCustomFields = libraryCustomFieldsVisbility?.data.length
    ? customFields?.filter(
        (customField) => !!libraryCustomFieldsVisbility?.data.find((field) => field.id === customField.id)?.visible,
      )
    : customFields;

  const onCustomFieldsClick = useCallback(
    () =>
      goToAssetPage({
        asset: file,
        query: {
          [QueryParamNames.amTab]: AssetModalPanel.FIELDS,
        },
        trackLocation: 'custom-field-click',
      }),
    [file, goToAssetPage],
  );

  const onDiscussionsClick = useCallback(
    () =>
      goToAssetPage({
        asset: file,
        query: {
          [QueryParamNames.amTab]: AssetModalPanel.COMMENTS,
        },
        trackLocation: 'comments-click',
      }),
    [file, goToAssetPage],
  );

  const onVersionsClick = useCallback(
    () =>
      goToAssetPage({
        asset: file,
        query: {
          [QueryParamNames.versions]: true,
        },
        trackLocation: 'versions-click',
      }),
    [file, goToAssetPage],
  );

  const onFileClick = useCallback(
    () =>
      goToAssetPage({
        asset: file,
        trackLocation: 'card-click',
      }),
    [file, goToAssetPage],
  );

  const onFileTitleCmdClick = useCallback(
    () =>
      openAssetInNewTab({
        asset: file,
        trackLocation: 'card-click',
      }),
    [file, openAssetInNewTab],
  );

  const canViewAssetVersions = canSeeAssetVersions(permissions);

  return {
    allCustomFields: filteredCustomFields,
    canViewCustomFields: canSeeAssetCustomFields(permissions),
    onCustomFieldsClick,
    onDiscussionsClick,
    onFileClick,
    onFileTitleCmdClick,
    onVersionsClick,
    shouldShowDiscussions: canViewAssetDiscussions(permissions),
    showVersionsIndicator: canViewAssetVersions && !!file.assetVersionCount && file.assetVersionCount > 1,
  };
};
